import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { X, ChevronLeft, ChevronRight, Pause, Play, MapPin, Eye, Banknote, Users, PiggyBank, Filter } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import RiyalIcon from './RiyalIcon';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function stripPrefix(n) { return n ? n.replace(/^(منطقة |المنطقة )/, '') : n; }

function fmtVal(v, lang) {
  if (!v && v !== 0) return '0';
  const n = Number(v);
  if (isNaN(n)) return '0';
  const isAr = lang === 'ar';
  if (n >= 1e9) return `${(n / 1e9).toFixed(2)}${isAr ? ' مليار' : ' B'}`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}${isAr ? ' مليون' : ' M'}`;
  if (n >= 1e4) return `${(n / 1e3).toFixed(1)}${isAr ? ' ألف' : ' K'}`;
  return n.toLocaleString('en-US');
}

const PresentationMode = ({ active, onToggle, zoomToCoordRef, onSelectProject, onReset, hidden }) => {
  const { language } = useLanguage();
  const isArabic = language === 'ar';
  const t = (ar, en) => isArabic ? ar : en;

  const [allFeatured, setAllFeatured] = useState([]);
  const [refData, setRefData] = useState({ cities: [], regions: [], categories: [], districts: [], subcategories: [] });
  const [regionStats, setRegionStats] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState('all');
  const [currentIdx, setCurrentIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [currentRegionStats, setCurrentRegionStats] = useState(null);
  const [currentRegionName, setCurrentRegionName] = useState('');
  const [showFilter, setShowFilter] = useState(false);
  const [viewingDetails, setViewingDetails] = useState(false);
  const timerRef = useRef(null);
  const dataLoaded = useRef(false);

  // Fetch data once
  useEffect(() => {
    if (dataLoaded.current || !API_URL) return;
    dataLoaded.current = true;
    Promise.all([
      fetch(`${API_URL}/api/projects/map-data`).then(r => r.json()),
      fetch(`${API_URL}/api/cities`).then(r => r.json()),
      fetch(`${API_URL}/api/regions`).then(r => r.json()),
      fetch(`${API_URL}/api/categories`).then(r => r.json()),
      fetch(`${API_URL}/api/districts`).then(r => r.json()),
      fetch(`${API_URL}/api/subcategories`).then(r => r.json()),
      fetch(`${API_URL}/api/region-statistics`).then(r => r.json()),
    ]).then(([projects, cities, regions, categories, districts, subcategories, stats]) => {
      // Sort featured projects: region → city → name (deterministic gallery order)
      const featured = projects.filter(p => p.is_featured);
      featured.sort((a, b) => {
        if (a.region_id !== b.region_id) return (a.region_id || '').localeCompare(b.region_id || '');
        if (a.city_id !== b.city_id) return (a.city_id || '').localeCompare(b.city_id || '');
        return (a.project_name_ar || '').localeCompare(b.project_name_ar || '');
      });
      setAllFeatured(featured);
      setRefData({ cities, regions, categories, districts, subcategories });
      setRegionStats(stats);
    console.log('[SHOWCASE] Data loaded. Featured:', featured.length);
    }).catch(e => console.error('[SHOWCASE] Fetch error:', e));
  }, []);

  // Filtered featured list by region
  const featured = useMemo(() => {
    if (selectedRegion === 'all') return allFeatured;
    return allFeatured.filter(p => p.region_id === selectedRegion);
  }, [allFeatured, selectedRegion]);

  // Regions that have featured projects
  const availableRegions = useMemo(() => {
    const regionIds = [...new Set(allFeatured.map(p => p.region_id))];
    return regionIds.map(id => {
      const r = refData.regions.find(rg => rg.id === id);
      return r ? { id, name: stripPrefix(isArabic ? r.name_ar : (r.name_en || r.name_ar)), count: allFeatured.filter(p => p.region_id === id).length } : null;
    }).filter(Boolean).sort((a, b) => b.count - a.count);
  }, [allFeatured, refData.regions, isArabic]);

  const buildProjectInfo = useCallback((proj) => {
    const { cities, regions, categories, districts, subcategories } = refData;
    const city = cities.find(c => c.id === proj.city_id);
    const region = regions.find(r => r.id === proj.region_id);
    const category = categories.find(c => c.id === proj.category_id);
    const district = districts.find(d => d.id === proj.district_id);
    const subcategory = subcategories.find(s => s.id === proj.subcategory_id);
    return {
      id: proj.id, project_name_ar: proj.project_name_ar, project_name_en: proj.project_name_en,
      status: proj.status, is_featured: proj.is_featured,
      latitude: proj.latitude, longitude: proj.longitude,
      cityName: isArabic ? city?.name_ar : (city?.name_en || city?.name_ar),
      regionName: stripPrefix(isArabic ? region?.name_ar : (region?.name_en || region?.name_ar)),
      categoryName: isArabic ? category?.name_ar : (category?.name_en || category?.name_ar),
      categoryColor: '#D6A84F',
      districtName: isArabic ? district?.name_ar : (district?.name_en || district?.name_ar),
      subcategoryName: isArabic ? subcategory?.name_ar : (subcategory?.name_en || subcategory?.name_ar),
      short_description: proj.short_description_ar || '',
    };
  }, [refData, isArabic]);

  const navigateTo = useCallback((idx) => {
    if (!featured.length || !zoomToCoordRef?.current) return;
    const p = featured[idx];
    if (!p) return;

    // Cinematic zoom: fly to project at zoom 14
    zoomToCoordRef.current(p.longitude, p.latitude, 14);

    // Open project detail
    onSelectProject(buildProjectInfo(p));

    // Region stats
    const region = refData.regions.find(r => r.id === p.region_id);
    const stats = regionStats.find(s => s.region_id === p.region_id);
    setCurrentRegionStats(stats || null);
    setCurrentRegionName(stripPrefix(isArabic ? region?.name_ar : region?.name_en) || '');
  }, [featured, zoomToCoordRef, onSelectProject, buildProjectInfo, refData.regions, regionStats, isArabic]);

  // Start on activation
  useEffect(() => {
    if (!active || !featured.length) return;
    setCurrentIdx(0);
    setPaused(false);
    const timeout = setTimeout(() => navigateTo(0), 400);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, featured.length]);

  // React to idx change
  useEffect(() => {
    if (!active || !featured.length) return;
    navigateTo(currentIdx);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIdx]);

  // Auto-advance timer (6s per project)
  useEffect(() => {
    if (!active || !featured.length || paused) {
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }
    timerRef.current = setInterval(() => {
      setCurrentIdx(prev => (prev + 1) % featured.length);
    }, 6000);
    return () => clearInterval(timerRef.current);
  }, [active, featured.length, paused]);

  // Detect when user clicks "عرض التفاصيل" in the project popup
  useEffect(() => {
    if (!active) return;
    const handler = (e) => {
      const btn = e.target.closest('[data-testid="project-details-btn"]');
      if (btn) {
        setPaused(true);
        setViewingDetails(true);
      }
    };
    document.addEventListener('click', handler, true);
    return () => document.removeEventListener('click', handler, true);
  }, [active]);

  // When project popup closes (selectedProject becomes null) during showcase → resume
  const selectedProjectRef = useRef(null);
  useEffect(() => {
    if (!active) return;
    // Detect: we HAD a project, now it's gone (user closed popup)
    if (selectedProjectRef.current && !viewingDetails) {
      // navigateTo will be called by the timer, nothing to do
    }
    if (viewingDetails && !onSelectProject) {
      // If viewing details was true but project was closed, resume
      setViewingDetails(false);
      setPaused(false);
      setTimeout(() => { if (featured[currentIdx]) navigateTo(currentIdx); }, 300);
    }
  }, [active, viewingDetails, featured, currentIdx, navigateTo, onSelectProject]);

  // Also detect popup close by watching for px-overlay removal
  useEffect(() => {
    if (!active || !viewingDetails) return;
    const observer = new MutationObserver(() => {
      const overlay = document.querySelector('.px-overlay');
      if (!overlay) {
        setViewingDetails(false);
        setPaused(false);
        setTimeout(() => { if (featured[currentIdx]) navigateTo(currentIdx); }, 300);
        observer.disconnect();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [active, viewingDetails, featured, currentIdx, navigateTo]);

  const handleExit = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    setPaused(false);
    setCurrentIdx(0);
    setCurrentRegionStats(null);
    setShowFilter(false);
    onSelectProject(null);
    if (onReset) onReset();
    onToggle();
  }, [onToggle, onSelectProject, onReset]);

  const goPrev = useCallback(() => { setCurrentIdx(prev => (prev - 1 + featured.length) % featured.length); }, [featured.length]);
  const goNext = useCallback(() => { setCurrentIdx(prev => (prev + 1) % featured.length); }, [featured.length]);

  // Keyboard controls
  useEffect(() => {
    if (!active) return;
    const handler = (e) => {
      if (e.key === 'Escape') handleExit();
      else if (e.key === 'ArrowLeft') goNext();
      else if (e.key === 'ArrowRight') goPrev();
      else if (e.key === ' ') { e.preventDefault(); setPaused(p => !p); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [active, handleExit, goNext, goPrev]);

  // Trigger button (hidden — handled by unified control panel)
  if (!active) {
    if (hidden) return null;
    return (
      <button className="pres-trigger" data-testid="presentation-trigger" onClick={onToggle}>
        <Play size={16} />
        <span>{t('وضع العرض', 'Presentation')}</span>
      </button>
    );
  }

  const currentProject = featured[currentIdx];

  return (
    <div className="pres-ui" data-testid="presentation-ui">
      <div className="pres-vignette" />

      {/* Top bar — hidden when viewing full project details */}
      {!viewingDetails && (
      <div className="pres-top-bar">
        <div className="pres-top-right">
          <button className="pres-top-btn" data-testid="presentation-exit" onClick={handleExit}>
            <X size={14} />
            <span>{t('خروج', 'Exit')}</span>
          </button>
        </div>

        <div className="pres-top-center">
          {/* Region stats — inline in top bar center */}
          {currentRegionStats && !viewingDetails && (
            <div className="pres-top-stats">
              <span className="pres-top-stats-region">{currentRegionName}</span>
              <span className="pres-top-stats-sep" />
              <Banknote size={12} style={{ color: '#D6A84F', flexShrink: 0 }} />
              <span className="pres-top-stats-val">{fmtVal(currentRegionStats.loans?.totalLoans, language)}</span>
              <span className="pres-top-stats-sep" />
              <Users size={12} style={{ color: '#BB8FCE', flexShrink: 0 }} />
              <span className="pres-top-stats-val">{fmtVal(currentRegionStats.empowerment_development?.totalnumberofbeneficiaries, language)}</span>
              <span className="pres-top-stats-sep" />
              <PiggyBank size={12} style={{ color: '#2FAE8F', flexShrink: 0 }} />
              <span className="pres-top-stats-val">{fmtVal(currentRegionStats.savings?.valueofsavings, language)}</span>
            </div>
          )}

          {/* Region filter */}
          <div style={{ position: 'relative' }}>
            <button
              className="pres-top-btn"
              data-testid="pres-region-filter-btn"
              onClick={() => setShowFilter(f => !f)}
            >
              <Filter size={13} />
              <span>{selectedRegion === 'all' ? t('كل المناطق', 'All Regions') : availableRegions.find(r => r.id === selectedRegion)?.name || ''}</span>
            </button>
            {showFilter && (
              <div className="pres-filter-dropdown" data-testid="pres-region-dropdown">
                <div
                  className={`pres-filter-item${selectedRegion === 'all' ? ' active' : ''}`}
                  onClick={() => { setSelectedRegion('all'); setCurrentIdx(0); setShowFilter(false); }}
                >
                  <MapPin size={12} />
                  <span>{t('كل المناطق', 'All Regions')}</span>
                  <span className="pres-filter-count">{allFeatured.length}</span>
                </div>
                {availableRegions.map(r => (
                  <div
                    key={r.id}
                    className={`pres-filter-item${selectedRegion === r.id ? ' active' : ''}`}
                    onClick={() => { setSelectedRegion(r.id); setCurrentIdx(0); setShowFilter(false); }}
                  >
                    <MapPin size={12} />
                    <span>{r.name}</span>
                    <span className="pres-filter-count">{r.count}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="pres-top-left">
          <div className="pres-counter" data-testid="presentation-counter">
            <span className="pres-counter-current">{currentIdx + 1}</span>
            <span className="pres-counter-sep">/</span>
            <span className="pres-counter-total">{featured.length}</span>
          </div>
        </div>
      </div>
      )}

      {/* Stats are now inline in the nav bar — big card removed */}

      {/* Bottom navigation — hidden when viewing full project details */}
      {!viewingDetails && (
      <div className="pres-nav-bar">
        <button className="pres-nav-btn" data-testid="presentation-prev" onClick={goPrev}>
          <ChevronRight size={20} />
        </button>

        <button className="pres-nav-btn pres-nav-play" data-testid="presentation-pause" onClick={() => setPaused(p => !p)}>
          {paused ? <Play size={18} /> : <Pause size={18} />}
        </button>

        <button className="pres-nav-btn" data-testid="presentation-next" onClick={goNext}>
          <ChevronLeft size={20} />
        </button>
      </div>
      )}

      {/* Progress bar — also hidden when viewing details */}
      {!viewingDetails && (
      <div className="pres-progress" data-testid="pres-progress">
        {featured.map((_, i) => (
          <div
            key={i}
            className={`pres-progress-seg${i === currentIdx ? ' active' : ''}${i < currentIdx ? ' done' : ''}`}
            onClick={() => setCurrentIdx(i)}
          />
        ))}
      </div>
      )}
    </div>
  );
};

export default PresentationMode;
